import { useEffect, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle } from "../roads/_roadsCommon";

const STATUS = { PASS: "#22c55e", FAIL: "#ef4444" };

/** Ch 22.2 — Retaining-wall designer with stability checks. */
export default function RetainingWallPanel({ projectId }) {
  const [palette, setPalette] = useState({ retaining_walls: [] });
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    name: "RW-1", wall_type: "cantilever",
    height_m: 5, base_width_m: 3.5, stem_thickness_m: 0.4,
    heel_m: 2, toe_m: 1.1, surcharge_kpa: 10,
    soil_phi_deg: 30, gamma_kn_m3: 18, qa_kpa: 200,
  });
  const [err, setErr] = useState("");

  const reload = async () => {
    try {
      const [p, r] = await Promise.all([
        api.get(`/projects/${projectId}/structures/palette`),
        api.get(`/projects/${projectId}/structures/retaining-walls`),
      ]);
      setPalette(p.data); setItems(r.data.retaining_walls || []);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    try {
      await api.post(`/projects/${projectId}/structures/retaining-walls`, form);
      reload();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/projects/${projectId}/structures/retaining-walls/${id}`); reload();
  };

  const num = (k) => (
    <input data-testid={`rw-${k}`} style={inputStyle} type="number" step="0.1"
      value={form[k]} onChange={(e) =>
        setForm({ ...form, [k]: parseFloat(e.target.value) || 0 })} />
  );

  return (
    <div data-testid="retaining-wall-panel" style={{ display: "grid",
            gridTemplateColumns: "320px 1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      <div style={{ ...card, padding: 8 }}>
        <div style={label}>New retaining wall</div>
        <div style={{ marginTop: 6, fontSize: 10 }}>Name</div>
        <input data-testid="rw-name" style={inputStyle} value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <div style={{ marginTop: 4, fontSize: 10 }}>Type</div>
        <select data-testid="rw-type" style={inputStyle} value={form.wall_type}
          onChange={(e) => setForm({ ...form, wall_type: e.target.value })}>
          {(palette.retaining_walls || []).map((w) =>
            <option key={w.id} value={w.id}>{w.label}</option>)}
        </select>
        <div style={{ marginTop: 4, fontSize: 10 }}>Height H (m)</div>{num("height_m")}
        <div style={{ marginTop: 4, fontSize: 10 }}>Base width · Stem t (m)</div>
        <div style={{ display: "flex", gap: 4 }}>{num("base_width_m")}{num("stem_thickness_m")}</div>
        <div style={{ marginTop: 4, fontSize: 10 }}>Heel · Toe (m)</div>
        <div style={{ display: "flex", gap: 4 }}>{num("heel_m")}{num("toe_m")}</div>
        <div style={{ marginTop: 4, fontSize: 10 }}>Surcharge (kPa) · φ° · γ kN/m³</div>
        <div style={{ display: "flex", gap: 4 }}>{num("surcharge_kpa")}{num("soil_phi_deg")}{num("gamma_kn_m3")}</div>
        <div style={{ marginTop: 4, fontSize: 10 }}>Allowable bearing q_a (kPa)</div>{num("qa_kpa")}
        <button data-testid="rw-create" style={{ ...btn, width: "100%", marginTop: 12 }}
          onClick={create}>+ Create wall</button>
      </div>

      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <div style={label}>Retaining walls ({items.length})</div>
        {items.map((w) => {
          const s = w.stability_check;
          const overall = s.FS_sliding_status === "PASS" &&
            s.FS_overturning_status === "PASS" && s.bearing_status === "PASS";
          return (
            <div key={w.id} data-testid={`rw-row-${w.id}`} style={{ padding: 8, marginTop: 6,
                  background: "rgba(255,255,255,0.04)", borderRadius: 4,
                  borderLeft: `3px solid ${overall ? STATUS.PASS : STATUS.FAIL}` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <b>{w.name}</b>
                <button style={{ ...btnGhost, padding: "1px 6px", fontSize: 10 }}
                  onClick={() => remove(w.id)}>×</button>
              </div>
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>
                {w.wall_type} · H={w.height_m}m · base {w.base_width_m}m · stem {w.stem_thickness_m}m
              </div>
              {/* Schematic SVG */}
              <svg viewBox={`-${w.toe_m + 0.5} -${w.height_m + 0.5} ${w.base_width_m + 1} ${w.height_m + 1.5}`}
                    style={{ width: 200, height: 140, marginTop: 6, background: "#0a1220" }}>
                {/* Soil */}
                <rect x={0} y={-w.height_m} width={w.heel_m} height={w.height_m} fill="#7a4f1b" opacity={0.5} />
                {/* Wall */}
                <rect x={w.toe_m} y={-w.height_m} width={w.stem_thickness_m} height={w.height_m}
                       fill="#999" stroke="#fff" strokeWidth={0.04} />
                {/* Base */}
                <rect x={0} y={0} width={w.base_width_m} height={0.5}
                       fill="#bbb" stroke="#fff" strokeWidth={0.04} />
                <line x1={-w.toe_m} y1={0.5} x2={w.base_width_m + 0.5} y2={0.5}
                       stroke="#7a4f1b" strokeWidth={0.05} strokeDasharray="0.1,0.1" />
              </svg>
              <div style={{ fontSize: 11, fontFamily: "monospace", marginTop: 4 }}>
                FS<sub>slide</sub> = <b style={{ color: STATUS[s.FS_sliding_status] }}>{s.FS_sliding}</b> ·
                FS<sub>overt</sub> = <b style={{ color: STATUS[s.FS_overturning_status] }}>{s.FS_overturning}</b> ·
                q<sub>max</sub> = <b style={{ color: STATUS[s.bearing_status] }}>{s.qmax_kpa}</b> kPa
                {s.tension_at_base && <span style={{ color: "#f59e0b", marginLeft: 4 }}>· tension at base!</span>}
              </div>
              <div style={{ fontSize: 11, color: overall ? STATUS.PASS : STATUS.FAIL, fontWeight: 600 }}>
                Overall: {overall ? "STABLE" : "UNSTABLE — increase base width or add key"}
              </div>
            </div>
          );
        })}
        {!items.length && <div style={{ color: "#888", fontSize: 11, padding: 12 }}>
          No retaining walls yet — define one.</div>}
      </div>

      <div style={{ ...card, padding: 8 }}>
        <div style={label}>Stability criteria</div>
        <p style={{ fontSize: 10, color: "#aaa", lineHeight: 1.6 }}>
          Rankine active-earth pressure with surcharge. Required: FS<sub>sliding</sub> ≥ 1.5,
          FS<sub>overturning</sub> ≥ 2.0, q<sub>max</sub> ≤ q<sub>a</sub>, no tension at base.
          References IS 14458 / IRC 78.
        </p>
        {err && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}
